import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { updateMyPayoutDetails } from '../../services/api';
import {
  TAX_CONDITIONS,
  PAYOUT_THRESHOLD,
  buildPayoutPayload,
  getApiErrorField,
  initialPayoutForm,
  invoiceTypeFor,
  validatePayoutForm,
} from '../../utils/partnerPortal';
import { formatCurrency } from '../../utils/formatCurrency';

const FIELDS = ['holderName', 'cuit', 'cbuOrAlias'];

/**
 * Datos de cobro del partner (RES-93, D6): titular, CUIT, CBU/CVU o alias y condición fiscal.
 * El pago es mensual por transferencia contra factura C (monotributo) o A (responsable inscripto).
 */
function PayoutDetailsForm({ payoutDetails, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => initialPayoutForm(payoutDetails));
  const [fieldError, setFieldError] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setForm(initialPayoutForm(payoutDetails));
  }, [payoutDetails]);

  const update = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (fieldError === name) setFieldError(null);
    setError(null);
    setSaved(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const invalidField = validatePayoutForm(form);
    if (invalidField) {
      setFieldError(invalidField);
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const result = await updateMyPayoutDetails(buildPayoutPayload(form));
      setSaved(true);
      if (onSaved) onSaved(result?.payoutDetails || null);
    } catch (err) {
      const field = getApiErrorField(err, 'INVALID_PAYOUT_DETAILS');
      if (field) setFieldError(field);
      else setError(t('partnerPortal.payout.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const invoiceType = invoiceTypeFor(form.taxCondition);

  return (
    <section className="pp-card pp-payout-card" aria-labelledby="pp-payout-title">
      <h2 id="pp-payout-title" className="pp-card-title">
        {t('partnerPortal.payout.title')}
      </h2>
      <p className="pp-muted">
        {t('partnerPortal.payout.explanation', { threshold: formatCurrency(PAYOUT_THRESHOLD) })}
      </p>

      <form className="pp-form" onSubmit={handleSubmit} noValidate>
        {FIELDS.map((name) => (
          <div key={name} className={`pp-field ${fieldError === name ? 'pp-field--error' : ''}`}>
            <label htmlFor={`pp-payout-${name}`}>{t(`partnerPortal.payout.fields.${name}`)}</label>
            <input
              id={`pp-payout-${name}`}
              name={name}
              type="text"
              value={form[name]}
              inputMode={name === 'cuit' ? 'numeric' : undefined}
              aria-invalid={fieldError === name ? 'true' : undefined}
              aria-describedby={`pp-payout-${name}-hint`}
              onChange={(e) => update(name, e.target.value)}
            />
            <span
              id={`pp-payout-${name}-hint`}
              className={fieldError === name ? 'pp-field-error' : 'pp-field-hint'}
            >
              {fieldError === name
                ? t(`partnerPortal.payout.errors.${name}`)
                : t(`partnerPortal.payout.hints.${name}`)}
            </span>
          </div>
        ))}

        <fieldset
          className={`pp-field pp-radio-group ${
            fieldError === 'taxCondition' ? 'pp-field--error' : ''
          }`}
          aria-invalid={fieldError === 'taxCondition' ? 'true' : undefined}
          data-field="taxCondition"
        >
          <legend>{t('partnerPortal.payout.fields.taxCondition')}</legend>
          {TAX_CONDITIONS.map((value) => (
            <label key={value} className="pp-radio">
              <input
                type="radio"
                name="taxCondition"
                value={value}
                checked={form.taxCondition === value}
                onChange={() => update('taxCondition', value)}
              />
              <span>{t(`partnerPortal.payout.taxConditions.${value}`)}</span>
            </label>
          ))}
          {fieldError === 'taxCondition' && (
            <span className="pp-field-error">{t('partnerPortal.payout.errors.taxCondition')}</span>
          )}
        </fieldset>

        {invoiceType && (
          <p className="pp-invoice-note" data-testid="pp-invoice-type" data-invoice={invoiceType}>
            {t('partnerPortal.payout.invoiceType', { type: invoiceType })}
          </p>
        )}

        {error && (
          <div className="error-message" role="alert">
            {error}
          </div>
        )}
        {saved && (
          <p className="pp-success" role="status">
            {t('partnerPortal.payout.saved')}
          </p>
        )}

        <div className="pp-form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? t('partnerPortal.payout.saving') : t('partnerPortal.payout.save')}
          </button>
        </div>
      </form>
    </section>
  );
}

export default PayoutDetailsForm;
